import { getWindow } from '@hzdg/dom-utils';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import isbot from 'isbot';
import { useEffect, useState } from 'react';

import localizedPathnames from '../../translations/pathnames';

import {
  addLocaleToUrlPathname,
  isLocale,
  Locale,
  safeParseLocale,
} from './locale';
import { usePageContext } from './use-page-context';

export const LOCAL_STORAGE_PREFERRED_LOCALE_KEY = 'localStoragePreferredLocale';

export const getLocalStoragePreferredLocale = () => {
  try {
    const value = window?.localStorage?.getItem(
      LOCAL_STORAGE_PREFERRED_LOCALE_KEY,
    );

    if (!isLocale(value)) {
      return null;
    }

    return value;
  } catch (error) {
    return null;
  }
};

export const setLocalStoragePreferredLocale = (value: Locale) =>
  window?.localStorage.setItem(LOCAL_STORAGE_PREFERRED_LOCALE_KEY, value);

const useBrowserPreferredLocale = () => {
  const window = getWindow();
  const [preferredLocale] = useState(
    safeParseLocale(window?.navigator.language),
  );

  return preferredLocale;
};
export const useRedirectToPreferredLocalePage = () => {
  const { locale } = usePageContext();
  const browserPreferredLocale = useBrowserPreferredLocale();
  const location = useLocation();
  const { englishPathname = location.pathname } = usePageContext<{
    englishPathname?: string;
  }>();

  useEffect(() => {
    // This function extracts the locale from the URL pathname, if it exists
    const getUrlLocale = () => {
      const pathname = window?.location.pathname;
      const pathParts = pathname?.split('/');
      const localePart = pathParts?.find((part) => part.includes('-'));
      if (!localePart) {
        return;
      }
      const [language, country = ''] = localePart.split('-');
      const formattedLocale = `${language.toLowerCase()}-${country.toUpperCase()}`;

      // TODO: Not correct
      if (localePart && localizedPathnames[formattedLocale]) {
        return formattedLocale;
      }
      return null;
    };
    const window = getWindow();

    if (!window) return;
    // TODO: Should we restore this?
    // if (isbot(window.navigator.userAgent)) return;

    // Check if the URL locale matches the current page locale
    const urlLocale = getUrlLocale();

    // Extract search (query parameters)
    const search = window?.location.search;

    if (urlLocale && urlLocale !== getLocalStoragePreferredLocale()) {
      setLocalStoragePreferredLocale(urlLocale as Locale);
      return; // Don't redirect, since we just updated the locale
    }

    // local storage preferred local overrides browser preferred local
    const activeLocale =
      getLocalStoragePreferredLocale() || browserPreferredLocale;

    // If no locale is set on localStorage, we should not navigate away from the
    // current url.
    const localizedPathname =
      localizedPathnames[englishPathname]?.[activeLocale];

    if (
      (locale || locale === Locale.EN_US) &&
      activeLocale !== locale &&
      localizedPathname
    ) {
      const newUrl =
        addLocaleToUrlPathname(localizedPathname, activeLocale) + search;

      // replace url state instead of pushing to not break back button behavior
      navigate(newUrl, {
        replace: true,
      });
    }
  }, [browserPreferredLocale, locale, englishPathname]);
};
